import type { AxiosResponse } from "axios";
import { GlobalAxios } from "../axios/axios-config";
import {
  IProfileReport,
  IProfileReportsPayload,
  IReportOption,
} from "../models/profile-report";
import { ISearchParams, ISearchReturn } from "../models/requests";
import MainApi from "./mainApi.service";

const PROFILE_REPORTS_ROUTE =
  process.env.REACT_APP_IDENTIY + "/api/v1/ProfileReports/";

// Reports are shared reference data, so we expect their catalog under Main like other base records.
const REPORT_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Report/";
const PROFILE_REPORTS_SEARCH_PAGE_SIZE = 1000;

export type IProfileReportsSearchParams = ISearchParams & {
  RoleId?: string;
};

const normalizeSearch = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase();

const mergeReportCatalog = (
  items: IProfileReport[],
  reportCatalog: IReportOption[],
) => {
  const reportCatalogById = new Map(
    reportCatalog.map((report) => [report.id, report]),
  );

  return items.map((item) => {
    const catalogReport = reportCatalogById.get(item.report?.id ?? item.reportId);

    if (!catalogReport) return item;

    return {
      ...item,
      report: {
        ...catalogReport,
        ...item.report,
        code: item.report?.code ?? catalogReport.code,
        name: item.report?.name ?? catalogReport.name,
      },
    };
  });
};

const filterProfileReportsBySearch = (
  items: IProfileReport[],
  searchBy?: string,
) => {
  const normalizedSearch = normalizeSearch(searchBy?.trim() ?? "");

  if (!normalizedSearch) return items;

  return items.filter((item) =>
    normalizeSearch(
      [item.report?.code, item.report?.name]
        .filter((value) => value !== undefined && value !== null && value !== "")
        .join(" "),
    ).includes(normalizedSearch),
  );
};

const paginateProfileReports = (
  items: IProfileReport[],
  pageNumber: number,
  pageSize: number,
): ISearchReturn<IProfileReport> => {
  const totalPages = Math.ceil(items.length / pageSize);
  const start = (pageNumber - 1) * pageSize;

  return {
    items: items.slice(start, start + pageSize),
    pageNumber,
    totalPages,
    totalCount: items.length,
    hasPreviousPage: pageNumber > 1,
    hasNextPage: pageNumber < totalPages,
  };
};

export const getRoleReports = (
  params?: IProfileReportsSearchParams
) => {
  return GlobalAxios.get<ISearchReturn<IProfileReport>>(PROFILE_REPORTS_ROUTE, {
    params,
  });
};

export const createRoleReports = (item: IProfileReportsPayload) => {
  return GlobalAxios.post(PROFILE_REPORTS_ROUTE, item);
};

export const updateRoleReports = ({
  id,
  item,
}: {
  id: string;
  item: IProfileReportsPayload;
}) => {
  return GlobalAxios.put(PROFILE_REPORTS_ROUTE + id, item);
};

export const deleteRoleReport = (id: string) => {
  return GlobalAxios.delete(PROFILE_REPORTS_ROUTE + id);
};

export const listReports = (params?: ISearchParams) => {
  return GlobalAxios.get<IReportOption[]>(`${REPORT_ROUTE}list`, {
    params,
  });
};

export class ProfileReportsService extends MainApi<IProfileReport> {
  private reportCatalogPromise: Promise<IReportOption[]> | null = null;

  constructor() {
    super(PROFILE_REPORTS_ROUTE);
  }

  deleteItemById = (id: string) => deleteRoleReport(id);

  getAll = async (
    searchParams?: IProfileReportsSearchParams,
  ): Promise<AxiosResponse<ISearchReturn<IProfileReport>>> => {
    const pageNumber = searchParams?.PageNumber ?? 1;
    const pageSize = searchParams?.PageSize ?? 25;
    const searchBy = searchParams?.SearchBy;

    if (!searchBy?.trim()) {
      const [response, reportCatalog] = await Promise.all([
        getRoleReports(searchParams),
        this.getReportCatalog(),
      ]);

      return {
        ...response,
        data: {
          ...response.data,
          items: mergeReportCatalog(response.data.items ?? [], reportCatalog),
        },
      };
    }

    const [response, reportCatalog] = await Promise.all([
      getRoleReports({
        ...searchParams,
        SearchBy: undefined,
        PageNumber: 1,
        PageSize: PROFILE_REPORTS_SEARCH_PAGE_SIZE,
      }),
      this.getReportCatalog(),
    ]);

    const filteredItems = filterProfileReportsBySearch(
      mergeReportCatalog(response.data.items ?? [], reportCatalog),
      searchBy,
    );

    return {
      ...response,
      data: paginateProfileReports(filteredItems, pageNumber, pageSize),
    };
  };

  private getReportCatalog = async () => {
    if (!this.reportCatalogPromise) {
      this.reportCatalogPromise = listReports().then(
        (response) => response.data ?? [],
      );
    }

    return this.reportCatalogPromise;
  };
}
